import { Controller } from "@hotwired/stimulus"
import { TagChipsMixin } from "../lib/tag_chips_mixin"

// Format enforced by ActsAsTaggableOn::Tag (config/initializers/acts_as_taggable_on.rb):
// length 2-50, lowercase letters/numbers/hyphens/underscores/spaces only.
// Mirrored here so a doomed-to-fail tag never even becomes a chip — the
// server-side check in Admin::Marketing::BlogPostsController#apply_tag_list
// stays as defense in depth (a client bypass can't 500 the update, and a chip
// added before this validation existed still round-trips cleanly).
const TAG_NAME_PATTERN = /^[a-z0-9\-_\s]+$/
const MIN_TAG_LENGTH = 2
const MAX_TAG_LENGTH = 50

// Blog-post tag chip input, acts-as-taggable-on comma format. The shared chip
// interaction (Enter/comma commits, × removes, blur commits pending text,
// Backspace on an empty field removes the last chip, hidden field kept in sync)
// lives in TagChipsMixin — shared with email_tags_controller.js. What is
// blog-specific and stays here: the `gl-blog-tags` chip markup, a comma-joined
// hidden `blog_post[tag_list]` value (a single-line acts-as-taggable-on list
// rather than one-per-line addresses), the client-side tag-name validation with
// its hint, and the addSuggestion() entry point.
//
// Admin::Marketing::BlogPostsController#apply_tag_list reads the hidden field
// and applies it with a stable tagger (the post's created_by), never the gem's
// ownerless `tag_list=` API.
export default class extends TagChipsMixin(Controller) {
  static targets = ["hidden", "input", "hint"]

  get chipNamespace() {
    return "gl-blog-tags"
  }

  get valueSeparator() {
    return ","
  }

  valueAllowed(value) {
    return value.length >= MIN_TAG_LENGTH && value.length <= MAX_TAG_LENGTH && TAG_NAME_PATTERN.test(value)
  }

  showValidationFeedback() {
    if (!this.hasHintTarget) return
    this.hintTarget.hidden = false
  }

  clearValidationFeedback() {
    if (!this.hasHintTarget) return
    this.hintTarget.hidden = true
  }

  // Public entry point for a suggestion source (blog_ai_writer_controller's
  // AI-suggested tags) to add a tag chip without going through the text input.
  // Deliberately skips valueAllowed() — suggestions come from a trusted
  // generator, not a typing user — but still de-duplicates and lowercases.
  addSuggestion(tag) {
    const value = String(tag || "").trim().toLowerCase()
    if (!value || this.chipValues().includes(value)) return

    this.appendChip(value)
    this.syncHidden()
  }
}
